import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FlaskConical, User, MapPin, Globe, FileText, Lock, ArrowLeft, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Footer } from '@/components/Footer';
import { authStorage } from '@/utils/authStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthorProfileEdit = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [profileForm, setProfileForm] = useState({
    town: '',
    country: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    alternative_names: []
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [newAltName, setNewAltName] = useState('');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const isAuthenticated = authStorage.isAuthorAuthenticated();
    const userData = authStorage.getAuthorUser();
    
    if (!isAuthenticated || !userData) {
      navigate('/author/login');
      return;
    }

    try {
      const user = userData;
      const response = await fetch(`${API_URL}/api/auth/me?user_id=${user.id}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch user data');
      }

      const data = await response.json();
      setUserData(data);
      setProfileForm({
        town: data.town || '',
        country: data.country || '',
        first_name: data.first_name || '',
        middle_name: data.middle_name || '',
        last_name: data.last_name || '',
        alternative_names: data.alternative_names || []
      });
    } catch (error) {
      void 0;
      toast.error('Failed to load profile data');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const authData = authStorage.getAuthorUser();
      
      const response = await fetch(`${API_URL}/api/auth/profile?user_id=${authData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileForm)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update profile');
      }

      const updatedUser = await response.json();
      
      authStorage.updateAuthorUser(updatedUser);
      
      toast.success('Profile updated successfully!');
      setTimeout(() => navigate('/author/dashboard'), 1500);
    } catch (error) {
      void 0;
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.new_password.length < 12) {
      toast.error('New password must be at least 12 characters');
      return;
    }

    setLoading(true);

    try {
      const authData = authStorage.getAuthorUser();
      
      const response = await fetch(`${API_URL}/api/auth/change-password?user_id=${authData.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to change password');
      }

      toast.success('Password changed successfully!');
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      void 0;
      toast.error(error.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const addAlternativeName = () => {
    if (newAltName.trim() && !profileForm.alternative_names.includes(newAltName.trim())) {
      setProfileForm({
        ...profileForm,
        alternative_names: [...profileForm.alternative_names, newAltName.trim()]
      });
      setNewAltName('');
    }
  };

  const removeAlternativeName = (index) => {
    const updated = [...profileForm.alternative_names];
    updated.splice(index, 1);
    setProfileForm({
      ...profileForm,
      alternative_names: updated
    });
  };

  if (!userData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center space-x-2">
              <FlaskConical className="h-8 w-8 text-blue-600" />
              <span className="text-xl font-bold text-slate-900">Edit Profile</span>
            </div>
            <Button variant="ghost" onClick={() => navigate('/author/dashboard')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Profile Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <User className="mr-2 h-5 w-5 text-blue-600" />
                Profile Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileUpdate} className="space-y-6">
                {/* Basic Info (Read-only) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4 border-b border-slate-200">
                  <div>
                    <Label className="text-slate-600">Username (Read-only)</Label>
                    <Input value={userData.username} disabled className="bg-slate-50" />
                  </div>
                  <div>
                    <Label className="text-slate-600">Email (Read-only)</Label>
                    <Input value={userData.email} disabled className="bg-slate-50" />
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900 flex items-center">
                    <MapPin className="mr-2 h-4 w-4 text-blue-600" />
                    Location
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="town">Town/City *</Label>
                      <Input
                        id="town"
                        value={profileForm.town}
                        onChange={(e) => setProfileForm({...profileForm, town: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country *</Label>
                      <Input
                        id="country"
                        value={profileForm.country}
                        onChange={(e) => setProfileForm({...profileForm, country: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Name Fields */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900 flex items-center">
                    <FileText className="mr-2 h-4 w-4 text-blue-600" />
                    Name Details (Optional - helps match your published articles)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="first_name">First Name</Label>
                      <Input
                        id="first_name"
                        placeholder="John"
                        value={profileForm.first_name}
                        onChange={(e) => setProfileForm({...profileForm, first_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="middle_name">Middle Name</Label>
                      <Input
                        id="middle_name"
                        placeholder="Edward"
                        value={profileForm.middle_name}
                        onChange={(e) => setProfileForm({...profileForm, middle_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name">Last Name</Label>
                      <Input
                        id="last_name"
                        placeholder="Smith"
                        value={profileForm.last_name}
                        onChange={(e) => setProfileForm({...profileForm, last_name: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* Alternative Names */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900 flex items-center">
                    <Globe className="mr-2 h-4 w-4 text-blue-600" />
                    Alternative Names (Optional)
                  </h3>
                  <p className="text-sm text-slate-600">
                    Add variations of your name as they appear in publications (e.g., "J.E. Smith", "John Smith", "Ivan Petrov")
                  </p>
                  
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter alternative name"
                      value={newAltName}
                      onChange={(e) => setNewAltName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addAlternativeName();
                        }
                      }}
                    />
                    <Button type="button" onClick={addAlternativeName} variant="outline">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {profileForm.alternative_names.length > 0 && (
                    <div className="space-y-2">
                      {profileForm.alternative_names.map((name, index) => (
                        <div key={index} className="flex items-center justify-between bg-slate-50 p-2 rounded-lg">
                          <span className="text-slate-700">{name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAlternativeName(index)}
                          >
                            <X className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? 'Saving...' : 'Save Profile Changes'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Password Change Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Lock className="mr-2 h-5 w-5 text-blue-600" />
                Change Password
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label htmlFor="current_password">Current Password</Label>
                  <Input
                    id="current_password"
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="new_password">New Password</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Min 12 characters, with uppercase, lowercase, number, and special character
                  </p>
                </div>

                <div>
                  <Label htmlFor="confirm_password">Confirm New Password</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                    required
                  />
                </div>

                <Button type="submit" disabled={loading} variant="outline" className="w-full">
                  {loading ? 'Changing...' : 'Change Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AuthorProfileEdit;
